import type {
  PurchaseLineView,
  PurchaseLinesPage,
} from '@obraplan/core/application/contracts/purchase-lines';
import { compareNames } from '@obraplan/core/domain/shared/naming';
import { err, ok, type Result } from '@obraplan/core/domain/shared/result';
import { formatBusinessDate } from '@/lib/format';
import { BASIS_LABEL, KIND_LABEL, ORIGIN_LABEL } from './purchase-model';

/**
 * A lista de compra como arquivo — por pavimento, serviço e data (Fase 26).
 *
 * Sai em CSV com **ponto e vírgula**, BOM e números com vírgula decimal, como o quantitativo
 * (`takeoff/exportar-quantitativo.ts`): é o que o Excel em português abre com um duplo clique.
 * A entrega do arquivo ao navegador (`baixarCsv`) é a do quantitativo; aqui só se monta o
 * conteúdo e se juntam as páginas da lista.
 *
 * O sistema não tem preço (RN-173, D7): a lista diz **o que**, **quanto** e **quando** — o
 * valor em R$ continua na planilha de origem.
 */

/** `1234.5` → `"1.234,5"`; a linha de rateio guarda seis casas, e o arquivo as leva. */
function numero(valor: number): string {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 6 });
}

/**
 * O campo, protegido: nome de material tem ponto e vírgula, aspas e quebra de linha com
 * frequência suficiente para que escapar não seja zelo excessivo.
 */
function campo(valor: string | number | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  const texto = typeof valor === 'number' ? numero(valor) : valor;
  return /[";\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto;
}

const CABECALHO = [
  'Torre',
  'Pavimento',
  'Disciplina',
  'Serviço',
  'Necessário em',
  'Código',
  'Material',
  // A classe vem na linha: é ela que deixa quem abre a planilha separar o pedido de compra
  // da contratação de empreiteiro (RN-349).
  'Classe',
  'Unidade',
  'Quantidade',
  'Quantidade provisória',
  'Base do cálculo',
  'Origem',
  'Código provisório',
] as const;

/** Nulo primeiro: a linha sem serviço vem antes das dos serviços da frente. */
function compararServico(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return compareNames(a, b);
}

/**
 * A ordem da lista de compra: torre, pavimento (pelo nível, e não pelo nome), disciplina,
 * serviço e a data em que o material precisa chegar. O código desempata.
 */
export function ordemDaListaDeCompra(a: PurchaseLineView, b: PurchaseLineView): number {
  return (
    compareNames(a.towerName, b.towerName) ||
    a.floorLevel - b.floorLevel ||
    compareNames(a.floorName, b.floorName) ||
    compareNames(a.disciplineName, b.disciplineName) ||
    compararServico(a.serviceName, b.serviceName) ||
    a.requiredDate.localeCompare(b.requiredDate) ||
    compareNames(a.materialCode, b.materialCode) ||
    a.id.localeCompare(b.id)
  );
}

export function listaDeCompraEmCsv(linhas: readonly PurchaseLineView[]): string {
  const saida = [CABECALHO.join(';')];

  for (const linha of [...linhas].sort(ordemDaListaDeCompra)) {
    saida.push(
      [
        campo(linha.towerName),
        campo(linha.floorName),
        campo(linha.disciplineName),
        campo(linha.serviceName),
        campo(formatBusinessDate(linha.requiredDate)),
        campo(linha.materialCode),
        campo(linha.materialName),
        campo(KIND_LABEL[linha.kind]),
        campo(linha.unit),
        campo(linha.quantity),
        campo(linha.quantityProvisional ? 'Sim' : 'Não'),
        campo(linha.allocationBasis === null ? null : BASIS_LABEL[linha.allocationBasis]),
        campo(ORIGIN_LABEL[linha.origin]),
        campo(linha.materialCodeProvisional ? 'Sim' : 'Não'),
      ].join(';'),
    );
  }

  // O BOM faz o Excel reconhecer UTF-8 sozinho; sem ele, "ALVENARIA CERÂMICA" chega
  // como "CERÃ‚MICA".
  return `\ufeff${saida.join('\r\n')}\r\n`;
}

/** O limite de linhas por página da lista (`listPurchaseLinesSchema`). */
export const EXPORT_PAGE_SIZE = 500;

/** Teto de páginas: 100 × 500 = 50.000 linhas, muito além de uma torre. */
const MAX_PAGES = 100;

/**
 * Junta todas as páginas da lista, com o mesmo filtro.
 *
 * A lista é paginada no servidor — a Torre 2 sozinha tem milhares de linhas —, e o arquivo
 * precisa de todas. Para quando junta o total que o servidor disse ter, ou quando uma página
 * vem vazia; a primeira recusa interrompe e é devolvida como está.
 */
export async function fetchAllPurchaseLines(
  fetchPage: (page: number) => Promise<Result<PurchaseLinesPage, { readonly message: string }>>,
): Promise<Result<readonly PurchaseLineView[], { readonly message: string }>> {
  const todas: PurchaseLineView[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const answer = await fetchPage(page);
    if (!answer.ok) return err(answer.error);
    todas.push(...answer.value.items);
    if (answer.value.items.length === 0 || todas.length >= answer.value.total) break;
  }
  return ok(todas);
}

/** `"lista-de-compra-torre-2-2026-10-02.csv"` — o nome diz o que é, de quê e de quando. */
export function nomeDoArquivoDeCompra(escopo: string, agora: Date): string {
  const dia = [
    String(agora.getFullYear()),
    String(agora.getMonth() + 1).padStart(2, '0'),
    String(agora.getDate()).padStart(2, '0'),
  ].join('-');
  const limpo = escopo
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `lista-de-compra-${limpo === '' ? 'obra' : limpo}-${dia}.csv`;
}
